/**
 * Tray (rules v2, "Ready trays", Bangkok L1511): a pre-made triple on one board cell. A woven
 * wicker tray in a wooden rim (one tile wide, with the tile's raised edge) holding its item three
 * times, fanned two at the back and one in front, and a "×3" badge on its corner, so the triple
 * reads by count and shape alone (the items use the colour-blind art like any tile). Dimmed like
 * a covered tile while a tile lies over it. Tapped, the three items lift and pop together right
 * there (never through the bag) and the empty tray folds away like an emptied dispenser.
 * Code-drawn like the machine and the gold frame: the texture is baked once per game.
 */
import type * as Phaser from 'phaser';
import { app } from '../game/context.js';
import { C, FONT, css } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { dur } from '../fx/motion.js';
import { KEY_BADGE, bakeLockArt } from './lock.js';

export const TRAY_ART = 'fx_tray';
const TW = 200;
const TH = 212;
/** the top face's centre in texture px (the rim spans 6..186): the tile art's origin, so a tray sits like a tile */
const FACE_Y = 96;
const COVERED_TINT = 0x999999; // same 40% darker as covered tiles (GP-01.1)

/** Where the three items sit, in tile edges from the cell centre: two at the back, one in front. */
export const TRAY_SLOTS = [
  { x: -0.17, y: -0.13 },
  { x: 0.17, y: -0.13 },
  { x: 0, y: 0.11 },
] as const;
/** Item edge in tile edges (a board tile's item is 0.76). */
export const TRAY_ITEM = 0.54;

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Draw the tray (shadow, raised edge, wooden rim with handle slots, woven floor) on a TW x TH canvas. */
export function drawTray(ctx: CanvasRenderingContext2D): void {
  const ink = css(C.ink);
  ctx.lineJoin = 'round';
  // shadow and the raised edge (like a tile's)
  rounded(ctx, 10, 22, 180, 182, 34);
  ctx.fillStyle = 'rgba(74,44,42,0.28)';
  ctx.fill();
  rounded(ctx, 10, 14, 180, 180, 34);
  ctx.fillStyle = css(C.kraftDark);
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = ink;
  ctx.stroke();
  // the rim: light wood
  rounded(ctx, 10, 6, 180, 180, 34);
  const g = ctx.createLinearGradient(0, 6, 0, 186);
  g.addColorStop(0, '#f6cf98');
  g.addColorStop(1, css(C.kraft));
  ctx.fillStyle = g;
  ctx.fill();
  ctx.stroke();
  // the woven floor, sunk inside the rim: a basket weave of short strokes, alternating per cell
  const [fx, fy, fw, fh] = [32, 28, 136, 136];
  rounded(ctx, fx, fy, fw, fh, 20);
  ctx.fillStyle = '#d9a066';
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = 'rgba(122,86,70,0.42)';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  const cell = 17;
  for (let r = 0; r * cell < fh; r++)
    for (let c = 0; c * cell < fw; c++) {
      const x0 = fx + c * cell;
      const y0 = fy + r * cell;
      for (let k = 1; k <= 3; k++) {
        const o = (k * cell) / 4;
        ctx.beginPath();
        if ((r + c) % 2) {
          ctx.moveTo(x0 + 3, y0 + o);
          ctx.lineTo(x0 + cell - 3, y0 + o);
        } else {
          ctx.moveTo(x0 + o, y0 + 3);
          ctx.lineTo(x0 + o, y0 + cell - 3);
        }
        ctx.stroke();
      }
    }
  // the rim's inner shadow on the floor
  ctx.fillStyle = 'rgba(74,44,42,0.18)';
  ctx.fillRect(fx, fy, fw, 9);
  ctx.restore();
  rounded(ctx, fx, fy, fw, fh, 20);
  ctx.lineWidth = 4;
  ctx.strokeStyle = ink;
  ctx.stroke();
  // handle slots on the left and right rim
  ctx.fillStyle = ink;
  for (const x of [16, TW - 26]) {
    rounded(ctx, x, 74, 10, 40, 5);
    ctx.fill();
  }
  // a shine along the top rim
  ctx.strokeStyle = 'rgba(255,255,255,0.75)';
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(52, 16);
  ctx.lineTo(118, 16);
  ctx.stroke();
}

/** Bake the tray texture (idempotent). */
export function bakeTrayArt(scene: Phaser.Scene): void {
  if (scene.textures.exists(TRAY_ART)) return;
  const c = document.createElement('canvas');
  c.width = TW;
  c.height = TH;
  drawTray(c.getContext('2d')!);
  scene.textures.addCanvas(TRAY_ART, c);
}

export class TrayView {
  /** the tray, its three items and the badge, at the cell centre */
  readonly root: Phaser.GameObjects.Container;
  private readonly base: Phaser.GameObjects.Image;
  readonly items: Phaser.GameObjects.Image[] = [];
  private readonly badge: Phaser.GameObjects.Graphics;
  private readonly count: Phaser.GameObjects.Text;
  /** lockKey: the key badge when the tray's item is the key */
  keyBadge: Phaser.GameObjects.Image | null = null;
  covered = false;
  /** popped (its items are lifting or gone, the tray folding or folded) */
  gone = false;
  itemId = '';
  private shade = 1;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly x: number,
    readonly y: number,
    private readonly tile: number,
    depth: number,
  ) {
    bakeTrayArt(scene);
    const T = tile;
    this.root = scene.add.container(x, y).setDepth(depth);
    this.base = scene.add.image(0, 0, TRAY_ART);
    this.base.setScale((T * 0.96) / TW).setOrigin(0.5, FACE_Y / TH);
    this.root.add(this.base);
    // the badge: a small mustard pill with "×3" on the top-right corner of the rim
    const bw = T * 0.4;
    const bh = T * 0.26;
    const bx = T * 0.33;
    const by = -T * 0.36;
    this.badge = scene.add.graphics();
    this.badge.fillStyle(C.ink, 1);
    this.badge.fillRoundedRect(bx - bw / 2, by - bh / 2 + 2 * vp.u, bw, bh, bh / 2);
    this.badge.fillStyle(C.mustard, 1);
    this.badge.lineStyle(Math.max(1.5, 2 * vp.u), C.ink, 1);
    this.badge.fillRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, bh / 2);
    this.badge.strokeRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, bh / 2);
    this.count = scene.add
      .text(bx, by, '×3', { fontFamily: FONT, fontSize: `${Math.max(10 * vp.u, Math.round(T * 0.19))}px`, fontStyle: '700', color: css(C.ink) })
      .setOrigin(0.5, 0.52);
    this.root.add([this.badge, this.count]);
  }

  /** The item the tray holds, three times (colour-blind art like any tile). */
  setItem(itemId: string): void {
    if (itemId === this.itemId) return;
    this.itemId = itemId;
    const f = app().art.item(itemId);
    const T = this.tile;
    TRAY_SLOTS.forEach((p, k) => {
      let img = this.items[k];
      if (!img) {
        img = this.scene.add.image(p.x * T, p.y * T, f.texture, f.frame);
        // under the badge (the back two first, the front one over them)
        this.root.addAt(img, 1 + k);
        this.items[k] = img;
      } else img.setTexture(f.texture, f.frame);
      img.setScale((T * TRAY_ITEM) / Math.max(img.width, img.height));
      // the back two lean out a little, so three items read at a glance
      img.setAngle(k === 0 ? -8 : k === 1 ? 8 : 0);
    });
    this.applyShade();
  }

  setCovered(covered: boolean, animate: boolean): void {
    if (covered === this.covered) return;
    this.covered = covered;
    const to = covered ? 0 : 1;
    const d = animate ? dur(180) : 0;
    if (!d) {
      this.shade = to;
      return this.applyShade();
    }
    this.scene.tweens.addCounter({
      from: this.shade,
      to,
      duration: d,
      onUpdate: (tw) => {
        this.shade = tw.getValue() ?? to;
        this.applyShade();
      },
    });
  }

  /** lockKey: the key badge (top-left corner) on or off, as on a tile of the key item. */
  setKey(on: boolean): void {
    if (on === !!this.keyBadge) return;
    if (!on) {
      this.keyBadge!.destroy();
      this.keyBadge = null;
      return;
    }
    bakeLockArt(this.scene);
    const k = this.scene.add.image(-this.tile * 0.3, -this.tile * 0.3, KEY_BADGE);
    k.setScale((this.tile * 0.36) / k.width);
    this.root.add(k);
    this.keyBadge = k;
    this.applyShade();
  }

  private applyShade(): void {
    const v = Math.round(0x99 + (0xff - 0x99) * this.shade);
    const tint = this.shade >= 1 ? 0xffffff : this.shade <= 0 ? COVERED_TINT : (v << 16) | (v << 8) | v;
    for (const o of [this.base, ...this.items, this.count]) o.setTint(tint);
    this.keyBadge?.setTint(tint);
    this.badge.setAlpha(this.shade >= 1 ? 1 : 0.6 + 0.4 * this.shade);
  }

  /**
   * The tap: the three items lift off the tray, gather and pop together where they rose (`onPop`
   * fires there with the burst: serve the ticket, float the coins), then the empty tray folds
   * away (`onGone` puffs at its spot). Under reduced motion it all runs shorter (dur); with
   * animations off the tray goes at once and `onPop` follows a microtask later, never inside
   * this call: like a bag pop, the batch's served/clutter/unlock feedback (queued on the pop
   * after the `popped` event) must be in place before it fires. Resolves once the tray is folded.
   */
  pop(onPop: (x: number, y: number) => void, onGone: ((x: number, y: number) => void) | null): Promise<void> {
    if (this.gone) return Promise.resolve();
    this.gone = true;
    this.lightUp();
    this.scene.tweens.killTweensOf(this.root);
    this.root.setAngle(0);
    const T = this.tile;
    const at = { x: this.x, y: this.y - T * 0.42 };
    const lift = dur(220);
    if (!lift) {
      this.root.setVisible(false);
      return Promise.resolve().then(() => onPop(at.x, at.y));
    }
    this.root.setDepth(7000);
    this.badge.setVisible(false);
    this.count.setVisible(false);
    this.keyBadge?.setVisible(false);
    return new Promise((resolve) => {
      // the items rise out of the tray and gather over it…
      this.items.forEach((img, k) => {
        this.scene.tweens.add({ targets: img, x: (k - 1) * T * 0.12, y: -T * 0.42, angle: 0, scale: img.scale * 1.15, duration: lift, delay: k * 30, ease: 'Back.easeOut' });
      });
      const squeeze = dur(150);
      this.scene.time.delayedCall(lift + 60, () => {
        // …squeeze into one and burst: the pop
        this.scene.tweens.add({ targets: this.items, x: 0, scale: 0, alpha: 0, duration: squeeze, ease: 'Cubic.easeIn' });
        this.scene.time.delayedCall(squeeze * 0.6, () => onPop(at.x, at.y));
        // the empty tray folds down and is gone
        const fold = dur(240);
        onGone?.(this.x, this.y);
        this.scene.tweens.add({
          targets: this.base,
          scaleY: this.base.scaleY * 0.15,
          scaleX: this.base.scaleX * 1.12,
          y: T * 0.3,
          alpha: 0,
          delay: squeeze,
          duration: fold,
          ease: 'Back.easeIn',
          onComplete: () => {
            this.root.setVisible(false);
            resolve();
          },
        });
      });
    });
  }

  /** Lit for the pop (a tray is only tapped uncovered; an undo never returns one). */
  private lightUp(): void {
    this.covered = false;
    this.shade = 1;
    this.applyShade();
  }

  /** Soft "nope" for a tap on a covered tray: a short wobble (GP-01.1). */
  nope(): void {
    const d = dur(260);
    if (!d || this.gone) return;
    this.scene.tweens.killTweensOf(this.root);
    this.root.setAngle(0);
    this.scene.tweens.add({ targets: this.root, angle: { from: -6, to: 6 }, duration: d / 4, yoyo: true, repeat: 1, ease: 'Sine.easeInOut', onComplete: () => this.root.setAngle(0) });
  }

  /** Still drawn on the board (not popped). */
  get shown(): boolean {
    return !this.gone && this.root.visible;
  }

  /** Win sweep: fade with the leftover tiles. */
  fadeOut(): void {
    if (this.gone) return;
    this.gone = true;
    const d = dur(260);
    if (!d) return void this.root.setVisible(false);
    this.scene.tweens.add({ targets: this.root, alpha: 0, duration: d, onComplete: () => this.root.setVisible(false) });
  }
}
